import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Labelbox,
  Table,
  FrameBox,
  InfoTable,
  Button,
  Modal,
  ContractInvoiceView,
} from "../../components";
import { Grid, Paper, TableCell, IconButton } from "@material-ui/core";
import FilterListOutlinedIcon from "@material-ui/icons/FilterListOutlined";
import { PrintRounded } from "@material-ui/icons";
import { useReactToPrint } from "react-to-print";
import "./payment.scss";
import { INVOICE_ACTION } from "../../redux/action";
import { dateFormatter } from "../../utils/common";
import { API_URL } from "../../utils/baseUrl";
import { useLocation } from "react-router-dom";
import axios from "axios";
import ZelleQR from "../../images/Zelle-QR-image.jpg";
import { font } from "../../resources/fonts/fontBase64";
import {
  getInvoicePaymentStatusLabel,
  isInvoiceCancelled,
} from "../../utils/invoiceStatusDisplay";
import { recordMatchesClientCategory } from "../../utils/clientDetailsCategory";
import { useClientCategoryTab } from "../../hooks/useClientCategoryTab";

const AGREEMENT_CONTENT = {
  title: "Terms & Conditions, Privacy Policy and Disclaimer",
  sections: [
    {
      title: "Terms & Conditions",
      items: [
        {
          title: "1. Acceptance of Terms",
          blocks: [
            {
              type: "paragraph",
              text: 'These Terms and Conditions ("Agreement") govern the provision of services by FinloTax Inc and FinloTax Services Pvt Ltd (collectively referred to as "FinloTax," "we," "us," or "our").',
            },
            { type: "paragraph", text: "By:" },
            {
              type: "bullets",
              items: [
                "Engaging our services",
                "Accepting an invoice",
                "Making payment",
                "Providing documents",
                "Using our website",
              ],
            },
            {
              type: "paragraph",
              text: "the Client agrees to be legally bound by these Terms.",
            },
          ],
        },
        {
          title: "2. Authorization to Provide Services",
          blocks: [
            { type: "paragraph", text: "The Client hereby:" },
            {
              type: "bullets",
              items: [
                "Authorizes FinloTax to perform the services outlined in invoices or engagement communications.",
                "Authorizes FinloTax to prepare, submit, or assist in filing documents with government authorities where required.",
              ],
            },
          ],
        },
        {
          title: "3. Client Responsibility for Accuracy of Information",
          blocks: [
            {
              type: "paragraph",
              text: "The Client acknowledges and agrees: All services performed by FinloTax rely entirely on information provided by the Client.",
            },
            {
              type: "paragraph",
              text: "The Client is solely responsible for:",
            },
            {
              type: "bullets",
              items: [
                "Accuracy of all submitted data",
                "Completeness of financial records",
                "Timely submission of documents",
                "Verification of tax returns before filing",
                "Compliance with applicable laws",
              ],
            },
            {
              type: "paragraph",
              text: "FinloTax shall not be responsible for:",
            },
            {
              type: "bullets",
              items: [
                "Errors caused by incomplete or incorrect client information",
                "Missing documentation",
                "Late submissions by the Client",
                "Failure to respond to requests",
              ],
            },
            {
              type: "paragraph",
              text: "Any penalties, interest, or legal consequences resulting from inaccurate or delayed client information remain the Client's responsibility.",
            },
          ],
        },
        {
          title: "4. Scope of Services",
          blocks: [
            {
              type: "paragraph",
              text: "FinloTax provides services including but not limited to:",
            },
            {
              type: "bullets",
              items: [
                "Tax preparation and filing",
                "Bookkeeping",
                "Payroll services",
                "Compliance filings",
                "Tax Planning services",
                "Business registration services",
              ],
            },
            {
              type: "paragraph",
              text: "FinloTax does not provide tax advice or legal advice unless explicitly stated in writing.",
            },
          ],
        },
        {
          title: "5. No Guarantee of Tax Outcome",
          blocks: [
            { type: "paragraph", text: "FinloTax does not guarantee:" },
            {
              type: "bullets",
              items: [
                "Refund amounts",
                "Tax savings",
                "Government approvals",
                "Processing timelines",
                "Audit outcomes",
              ],
            },
            {
              type: "paragraph",
              text: "All tax outcomes depend on:",
            },
            {
              type: "bullets",
              items: [
                "Client-provided information",
                "Applicable law",
                "Government review",
              ],
            },
          ],
        },
        {
          title: "6. Fees and Payment Terms",
          blocks: [
            { type: "paragraph", text: "The Client confirms:" },
            {
              type: "bullets",
              items: [
                "The fees stated in the invoice are accurate and acceptable.",
                "Fees must be paid within 3 business days of invoice receipt.",
              ],
            },
            {
              type: "paragraph",
              text: "Failure to pay may result in:",
            },
            {
              type: "bullets",
              items: [
                "Service suspension",
                "Filing delays",
                "Late charges",
                "Automatic debit from Client account where authorized",
              ],
            },
            {
              type: "paragraph",
              text: "All fees are non-refundable once work has begun.",
            },
          ],
        },
        {
          title: "7. Strict No Refund Policy",
          blocks: [
            { type: "paragraph", text: "Refunds will not be issued for:" },
            {
              type: "bullets",
              items: [
                "Once work begins",
                "Tax return is prepared, or the services are completed",
                "Filed returns",
                "Submitted applications",
                "Consultation services",
                "Work delayed due to client inaction",
                "Client dissatisfaction after services performed",
              ],
            },
            {
              type: "paragraph",
              text: "Refund requests may only be considered if no work has begun.",
            },
          ],
        },
        {
          title: "8. Filing and Deadline Disclaimer",
          blocks: [
            { type: "paragraph", text: "FinloTax prepares filings based on:" },
            {
              type: "bullets",
              items: [
                "Information received",
                "Applicable laws",
                "Standard professional practices",
              ],
            },
            {
              type: "paragraph",
              text: "FinloTax shall not be liable for:",
            },
            {
              type: "bullets",
              items: [
                "Missed deadlines due to late client response",
                "Government processing delays",
                "System outages",
                "Postal or electronic filing delays",
                "Changes in law or regulations",
              ],
            },
            { type: "paragraph", text: "The Client remains responsible for:" },
            {
              type: "bullets",
              items: [
                "Timely submission of information",
                "Tax payment deadlines",
                "Compliance with applicable laws",
              ],
            },
          ],
        },
        {
          title: "9. Government Notices",
          blocks: [
            {
              type: "paragraph",
              text: "FinloTax shall not be responsible for IRS notices, State tax notices, or audit notices unless such notice arise solely from proven gross negligence of FinloTax. Client agrees to promptly notify FinloTax of any notices received.",
            },
          ],
        },
        {
          title: "10. Limitation of Liability",
          blocks: [
            {
              type: "paragraph",
              text: "To the maximum extent permitted by law, FinloTax's total liability is limited to fees paid by the Client during the preceding 2 months.",
            },
            { type: "paragraph", text: "FinloTax shall not be liable for:" },
            {
              type: "bullets",
              items: [
                "Incidental damages",
                "Indirect damages",
                "Consequential damages",
                "Loss of profits",
                "Loss of business",
                "Loss of reputation",
                "Government penalties",
                "Interest assessments",
              ],
            },
            {
              type: "paragraph",
              text: "This limitation applies regardless of legal theory.",
            },
          ],
        },
        {
          title: "11. Errors and Omissions Protection",
          blocks: [
            { type: "paragraph", text: "FinloTax shall not be liable for:" },
            {
              type: "bullets",
              items: [
                "Errors resulting from incomplete client data",
                "Errors resulting from inaccurate documentation",
                "Errors caused by third-party software",
                "Changes in tax law",
                "System failures",
                "Government calculation adjustments",
              ],
            },
            {
              type: "paragraph",
              text: "Clients must review all filings prior to submission. Approval constitutes acceptance of responsibility.",
            },
          ],
        },
        {
          title: "12. Third-Party Systems Disclaimer",
          blocks: [
            {
              type: "paragraph",
              text: "FinloTax relies on third-party systems including:",
            },
            {
              type: "bullets",
              items: [
                "Tax software",
                "Payment processors",
                "Cloud storage",
                "Government portals",
              ],
            },
            {
              type: "paragraph",
              text: "FinloTax shall not be responsible for:",
            },
            {
              type: "bullets",
              items: [
                "Third-party outages",
                "Processing failures",
                "System downtime",
                "Payment gateway issues",
              ],
            },
          ],
        },
        {
          title: "13. Offshore Processing Disclosure",
          blocks: [
            {
              type: "paragraph",
              text: "Client acknowledges that certain services may be supported by FinloTax Services Pvt Ltd (India). These services may include:",
            },
            {
              type: "bullets",
              items: [
                "Data entry",
                "Document processing",
                "Return preparation support",
                "Administrative review",
              ],
            },
            { type: "paragraph", text: "All offshore staff are bound by:" },
            {
              type: "bullets",
              items: [
                "Confidentiality agreements",
                "Security controls",
                "Data protection policies",
              ],
            },
            {
              type: "paragraph",
              text: "Client expressly consents to offshore support.",
            },
          ],
        },
        {
          title: "14. Client Review Requirement",
          blocks: [
            { type: "paragraph", text: "Before filing, the Client must:" },
            {
              type: "bullets",
              items: [
                "Review all prepared documents",
                "Confirm accuracy",
                "Approve submission",
              ],
            },
            {
              type: "paragraph",
              text: "Failure to review does not transfer responsibility to FinloTax.",
            },
          ],
        },
        {
          title: "15. Communication Policy",
          blocks: [
            { type: "paragraph", text: "FinloTax may communicate via:" },
            {
              type: "bullets",
              items: ["Email", "Phone", "SMS", "Client portal"],
            },
            {
              type: "paragraph",
              text: "Client agrees to receive service-related communications.",
            },
          ],
        },
        {
          title: "16. Record Retention",
          blocks: [
            {
              type: "paragraph",
              text: "Clients remain responsible for maintaining:",
            },
            {
              type: "bullets",
              items: ["Financial records", "Supporting documentation"],
            },
            {
              type: "paragraph",
              text: "FinloTax retains records only as required by law.",
            },
          ],
        },
        {
          title: "17. Service Suspension",
          blocks: [
            {
              type: "paragraph",
              text: "FinloTax reserves the right to suspend services if:",
            },
            {
              type: "bullets",
              items: [
                "Payment is overdue (Payment must be made before submitting the tax returns)",
                "Required documents are not provided",
                "Client fails to cooperate",
              ],
            },
          ],
        },
        {
          title: "18. Indemnification Clause",
          blocks: [
            {
              type: "paragraph",
              text: "The Client agrees to indemnify and hold harmless FinloTax from claims arising from:",
            },
            {
              type: "bullets",
              items: [
                "Inaccurate information",
                "Client negligence",
                "Client legal violations",
                "Failure to comply with law",
              ],
            },
          ],
        },
        {
          title: "19. Force Majeure",
          blocks: [
            {
              type: "paragraph",
              text: "FinloTax shall not be liable for delays caused by:",
            },
            {
              type: "bullets",
              items: [
                "Natural disasters",
                "Cyber incidents",
                "Government shutdowns",
                "Utility failures",
                "Events beyond reasonable control",
              ],
            },
          ],
        },
        {
          title: "20. Governing Law",
          blocks: [
            {
              type: "paragraph",
              text: "This Agreement shall be governed in accordance with the laws of the State of California, USA. All disputes shall be resolved in California courts.",
            },
          ],
        },
        {
          title: "21. Updates to Terms",
          blocks: [
            {
              type: "paragraph",
              text: "FinloTax reserves the right to update these Terms at any time. Continued use of services constitutes acceptance.",
            },
          ],
        },
        {
          title: "22. Contact Information",
          blocks: [
            {
              type: "bullets",
              items: [
                "FinloTax Inc (USA): info@finlotax.com",
                "FinloTax Services Pvt Ltd (India): info@finlotax.com",
              ],
            },
          ],
        },
      ],
    },
    {
      title: "Privacy Policy",
      items: [
        {
          title: "1. Introduction",
          blocks: [
            {
              type: "paragraph",
              text: 'FinloTax Inc (United States) and FinloTax Services Pvt Ltd (India) (collectively, "FinloTax," "we," "us," or "our") respect your privacy and are committed to protecting your personal, financial, and tax-related information.',
            },
            {
              type: "paragraph",
              text: "This Privacy Policy explains how we collect, use, store, disclose, and safeguard your information when you:",
            },
            {
              type: "bullets",
              items: [
                "Use our tax preparation services",
                "Engage our bookkeeping, payroll, advisory, or compliance services",
                "Visit our website",
                "Communicate with us through email, phone, SMS, or online platforms",
              ],
            },
            {
              type: "paragraph",
              text: "By using our services, you agree to the terms of this Privacy Policy.",
            },
          ],
        },
        {
          title: "2. Entities Covered",
          blocks: [
            {
              type: "paragraph",
              text: "This Privacy Policy applies to FinloTax Inc (USA), a United States-based tax preparation and financial services company, and FinloTax Services Pvt Ltd (India), an affiliated India-based entity providing:",
            },
            {
              type: "bullets",
              items: [
                "Administrative support",
                "Data entry",
                "Tax return preparation assistance",
                "Document processing",
                "Quality review services",
              ],
            },
            {
              type: "paragraph",
              text: "Both entities operate under unified confidentiality and security standards.",
            },
          ],
        },
        {
          title: "3. Information We Collect",
          blocks: [
            {
              type: "paragraph",
              text: "We may collect the following categories of information:",
            },
            {
              type: "bullets",
              items: [
                "Personal Identifiers: Full name, address, date of birth, Social Security Number (SSN), Individual Taxpayer Identification Number (ITIN), phone number, and email address.",
                "Financial and Tax Information: Income information, expense records, tax forms, bank account details, payroll records, and business financial data.",
                "Employment and Business Information: Employer details, business ownership records, and entity documentation.",
                "Government Identification: Driver's license, passport, and other government-issued ID.",
                "Communication Records: Emails, phone calls, SMS messages, and client portal messages.",
                "Digital Usage Data: IP address, browser type, device information, cookies, and usage activity.",
              ],
            },
          ],
        },
        {
          title: "4. How We Use Your Information",
          blocks: [
            { type: "paragraph", text: "We use your information to:" },
            {
              type: "bullets",
              items: [
                "Prepare and file tax returns",
                "Provide bookkeeping and payroll services",
                "Deliver tax advisory and compliance services",
                "Respond to inquiries and support requests",
                "Schedule appointments and reminders",
                "Maintain regulatory compliance",
                "Improve service quality",
                "Prevent fraud and ensure data security",
              ],
            },
            {
              type: "paragraph",
              text: "We only use your information for legitimate business purposes.",
            },
          ],
        },
        {
          title: "5. Cross-Border Processing and Offshore Support",
          blocks: [
            {
              type: "paragraph",
              text: "To provide efficient and cost-effective services, your information may be accessed, processed, and reviewed in the United States and India.",
            },
            {
              type: "paragraph",
              text: "Specifically, your information may be handled by trained personnel of FinloTax Services Pvt Ltd (India) who support tax preparation and administrative functions.",
            },
            { type: "paragraph", text: "These services include:" },
            {
              type: "bullets",
              items: [
                "Data entry",
                "Document processing",
                "Return preparation support",
                "Administrative review",
                "Quality control",
              ],
            },
            { type: "paragraph", text: "Safeguards for offshore processing." },
            {
              type: "paragraph",
              text: "We implement strict controls including:",
            },
            {
              type: "bullets",
              items: [
                "Role-based access controls",
                "Encryption of data in transit and at rest",
                "Confidentiality agreements",
                "Limited data access permissions",
                "Secure login authentication",
                "Monitoring",
                "Audit logs",
              ],
            },
            {
              type: "paragraph",
              text: " All personnel are contractually bound to confidentiality obligations consistent with U.S. tax preparer standards.",
            },
          ],
        },
        {
          title: "6. IRC §7216 Tax Return Confidentiality Notice",
          blocks: [
            {
              type: "paragraph",
              text: "By engaging FinloTax services, you acknowledge and expressly consent that:",
            },
            {
              type: "bullets",
              items: [
                "Your tax return information may be disclosed to FinloTax Services Pvt Ltd (India) for tax preparation, bookkeeping, and administrative support.",
                "Such disclosure is strictly limited to tax preparation and related services.",
                "All personnel handling your information are bound by strict confidentiality requirements.",
              ],
            },
          ],
        },
        {
          title:
            "7. Legal Basis for Processing (CCPA / CPRA – California Residents)",
          blocks: [
            {
              type: "paragraph",
              text: "For California residents, we collect personal information for:",
            },
            {
              type: "bullets",
              items: [
                "Service delivery",
                "Legal compliance",
                "Fraud prevention",
                "Customer support",
              ],
            },
            {
              type: "paragraph",
              text: "FinloTax:",
            },
            {
              type: "bullets",
              items: [
                "Does not sell personal information",
                "Does not rent personal information",
                "Does not share personal information for marketing purposes",
              ],
            },
            ,
            {
              type: "paragraph",
              text: "We may share data only with authorized service providers necessary to deliver services.",
            },
          ],
        },
        {
          title: "8. Disclosure of Information",
          blocks: [
            { type: "paragraph", text: "We may disclose information to:" },
            {
              type: "bullets",
              items: [
                "FinloTax personnel",
                "Authorized contractors",
                "Technology vendors",
                "Cloud storage providers",
                "Tax software providers",
                "Legal authorities (if required by law)",
              ],
            },
            {
              type: "paragraph",
              text: "All disclosures are limited to necessary purposes.",
            },
          ],
        },
        {
          title: "9. Third-Party Service Providers",
          blocks: [
            {
              type: "paragraph",
              text: "We use trusted vendors to support operations, including:",
            },
            {
              type: "bullets",
              items: [
                "Tax preparation software",
                "Secure client portals",
                "Cloud storage providers",
                "Accounting software platforms",
                "Communication systems",
                "CRM systems",
              ],
            },
            {
              type: "paragraph",
              text: "These providers are contractually required to:",
            },
            {
              type: "bullets",
              items: [
                "Maintain confidentiality",
                "Implement security controls",
                "Protect personal information",
              ],
            },
          ],
        },
        {
          title: "10. Data Retention",
          blocks: [
            { type: "paragraph", text: "We retain information:" },
            {
              type: "bullets",
              items: [
                "As required under IRS regulations",
                "As required under applicable state laws",
                "As required for legal compliance",
              ],
            },
            {
              type: "paragraph",
              text: "Typical retention period: 6 years or longer, if legally required.",
            },
          ],
        },
        {
          title: "11. Data Security",
          blocks: [
            {
              type: "paragraph",
              text: "We implement industry-standard safeguards, including:",
            },
            {
              type: "bullets",
              items: [
                "Encryption technology",
                "Multi-factor authentication (MFA)",
                "Secure file transfer systems",
                "Firewall protection",
                "Access restrictions",
                "Employee confidentiality training",
                "System monitoring",
                "Secure backups",
                "Periodic security reviews",
              ],
            },
            {
              type: "paragraph",
              text: "No system is completely secure, but we take reasonable steps to protect your information.",
            },
          ],
        },
        {
          title: "12. Data Breach Notification",
          blocks: [
            {
              type: "paragraph",
              text: "In the event of a data breach affecting personal information, we will:",
            },
            {
              type: "bullets",
              items: [
                "Investigate the incident",
                "Take corrective actions",
                "Notify affected individuals",
                "Notify regulatory authorities when required",
              ],
            },
          ],
        },
        {
          title: "13. Cookies and Tracking Technologies",
          blocks: [
            {
              type: "paragraph",
              text: "We use cookies and similar technologies to:",
            },
            {
              type: "bullets",
              items: [
                "Improve website functionality",
                "Analyze usage trends",
                "Enhance user experience",
              ],
            },
            {
              type: "paragraph",
              text: "Cookies may collect: ",
            },
            {
              type: "bullets",
              items: [
                "IP address",
                "Device information",
                "Browser type",
                "Pages visited",
              ],
            },
            ,
            {
              type: "paragraph",
              text: "You may disable cookies in your browser settings.",
            },
          ],
        },
        {
          title: "14. Your Privacy Rights",
          blocks: [
            {
              type: "paragraph",
              text: "Depending on your location, you may have rights to:",
            },
            {
              type: "bullets",
              items: [
                "Access personal information",
                "Correct inaccurate data",
                "Request deletion (where legally permitted)",
                "Request disclosure of collected data",
                "Request limitation of data processing",
              ],
            },
            {
              type: "paragraph",
              text: "Requests may be submitted to privacy@finlotax.com.",
            },
          ],
        },
        {
          title: "15. International Users",
          blocks: [
            {
              type: "paragraph",
              text: "If you are located outside the United States, you acknowledge:",
            },
            {
              type: "bullets",
              items: [
                "Your information may be transferred internationally",
                "Data protection laws may differ",
                "You consent to such transfers",
              ],
            },
          ],
        },
        {
          title: "16. Children's Privacy",
          blocks: [
            {
              type: "paragraph",
              text: "Our services are not directed to individuals under 18 years of age. We do not knowingly collect data from minors.",
            },
          ],
        },
        {
          title: "17. SMS Policy",
          blocks: [
            {
              type: "paragraph",
              text: "FinloTax may use SMS communications to provide service-related notifications.",
            },
            { type: "paragraph", text: "SMS Usage:" },
            {
              type: "paragraph",
              text: "FinloTax uses the information you provide to contact you regarding:",
            },
            {
              type: "bullets",
              items: [
                "Tax-related services and updates",
                "Appointment reminders",
                "Filing notifications",
                "Customer support responses",
                "Service alerts",
              ],
            },
            {
              type: "paragraph",
              text: "We collect mobile phone numbers only with user consent.",
            },
            { type: "paragraph", text: "SMS Data Protection:" },
            {
              type: "bullets",
              items: [
                "We do not sell mobile numbers",
                "We do not rent SMS data",
                "We do not share SMS consent data with third parties for marketing",
              ],
            },
            {
              type: "paragraph",
              text: "Information is used only to provide service and respond to inquiries.",
            },
            { type: "paragraph", text: "SMS Opt-Out:" },
            { type: "paragraph", text: "User may opt out at any time by:" },
            {
              type: "bullets",
              items: ["Replying STOP, or", "Emailing info@finlotax.com"],
            },
            { type: "paragraph", text: "SMS Support" },
            {
              type: "paragraph",
              text: "User may  replay HELP for assistance. Additional SMS terms:",
            },
            {
              type: "bullets",
              items: [
                "Message frequency varies and message and data rates may apply.",
              ],
            },
          ],
        },
        {
          title: "18. Consent",
          blocks: [
            {
              type: "paragraph",
              text: "By using FinloTax services, you acknowledge:",
            },
            {
              type: "bullets",
              items: [
                "This Privacy Policy",
                "Cross-border data transfer",
                "Offshore processing in India",
                "Use of SMS communications (if opted in)",
              ],
            },
            {
              type: "paragraph",
              text: "Where required, additional written consent may be collected.",
            },
          ],
        },
        {
          title: "19. Changes to This Privacy Policy",
          blocks: [
            {
              type: "paragraph",
              text: "We may update this policy periodically. Updates will include a revised effective date and updated content. The latest version will always be available on our website.",
            },
          ],
        },
        {
          title: "20. Contact Information",
          blocks: [
            {
              type: "bullets",
              items: [
                "FinloTax Inc (USA): 1190 Miraloma Way Ste P, Sunnyvale CA 94085",
                "FinloTax Inc (USA): 1811 Santa Rita Rd, Ste 200 Pleasanton, CA 94566",
                "FinloTax Inc (USA): 280 Towerview ct, Suite 112, Cary NC 27513",
                "FinloTax Inc (USA): info@finlotax.com",
                "FinloTax Services Pvt Ltd (India): Bengaluru, Karnataka 560011",
                "FinloTax Services Pvt Ltd (India): Sirsi, Karnataka 581401",
                "FinloTax Services Pvt Ltd (India): info@finlotax.com",
                "Privacy inquiries: privacy@finlotax.com",
              ],
            },
          ],
        },
      ],
    },
    {
      title: "Disclaimer",
      items: [
        {
          title: "Overview",
          intro: [
            "FinloTax Inc. provides tax preparation services to individuals and businesses in California. While we strive to provide accurate and reliable tax preparation services, it's important to understand the following:",
          ],
        },
        {
          title: "Non-Professional Advice",
          intro: [
            "The information provided by FinloTax Inc. and our representatives during the tax preparation process is for general informational purposes only. It should not be considered as professional advice or relied upon as a substitute for consultation with a qualified tax professional or financial advisor. We are a tax preparation firm, not a CPA firm and we do not provide audit and attestation services.",
          ],
        },
        {
          title: "Limitations of Service",
          intro: [
            "Our tax preparation services are based on the information provided by clients and the accuracy of the documentation provided. We do not verify the accuracy or completeness of the information provided by clients, and we do not guarantee the accuracy or completeness of the tax returns prepared.",
          ],
        },
        {
          title: "Client Responsibility",
          intro: [
            "Clients are responsible for providing accurate and complete information necessary for tax preparation. Any discrepancies or inaccuracies in the provided information are the responsibility of the client. Clients should review their tax returns and related documents carefully before filing.",
          ],
        },
        {
          title: "Changes in Tax Laws",
          intro: [
            "Tax laws and regulations are subject to change, and the information provided during the tax preparation process may become outdated or inaccurate due to changes in tax laws. We do not guarantee that the information provided reflects the most current tax laws or interpretations.",
          ],
        },
        {
          title: "Consultation with Professionals",
          intro: [
            "We strongly recommend that clients consult with a qualified tax professional or financial advisor regarding their specific tax situation, financial goals, and any legal or tax implications. Our services should be used as part of a comprehensive tax strategy developed with the assistance of professional advisors.",
          ],
        },
        {
          title: "No Client Relationship",
          intro: [
            "The use of our tax preparation services does not create a professional relationship between FinloTax Inc and the client beyond the scope of the specific services provided. We are not engaged as a tax advisor, accountant, or financial planner unless explicitly agreed upon in writing.",
          ],
        },
        {
          tail: "By using our tax preparation services, you acknowledge that you have read, understood, and agreed to this disclaimer. If you have any questions or concerns about our services or this disclaimer, please contact us at support@finlotax.com.",
        },
      ],
    },
  ],
};

const EMAIL_REGEX = /([A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,})/gi;

const renderTextWithEmails = (text) => {
  if (typeof text !== "string") return text;

  const parts = [];
  let lastIndex = 0;

  text.replace(EMAIL_REGEX, (match, _group, offset) => {
    if (offset > lastIndex) {
      parts.push(text.slice(lastIndex, offset));
    }

    parts.push(
      <a
        key={`${match}-${offset}`}
        href={`mailto:${match}`}
        target="_blank"
        rel="noreferrer noopener"
        className="agreement-email-link"
      >
        {match}
      </a>,
    );

    lastIndex = offset + match.length;
    return match;
  });

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length ? parts : text;
};

const renderAgreementBlocks = (blocks = [], itemTitle) => {
  return blocks.map((block, blockIndex) => {
    if (block.type === "bullets") {
      return (
        <ul
          key={`${itemTitle}-block-${blockIndex}`}
          className="agreement-modal-list"
        >
          {block.items.map((bullet) => (
            <li key={bullet} className="agreement-modal-list-item">
              {renderTextWithEmails(bullet)}
            </li>
          ))}
        </ul>
      );
    }

    return (
      <p
        key={`${itemTitle}-block-${blockIndex}`}
        className="agreement-modal-paragraph"
      >
        {renderTextWithEmails(block.text)}
      </p>
    );
  });
};

const ContractPayment = (props) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const [pendingInvoices, setPendingInvoices] = React.useState([]);
  const [paidInvoices, setPaidInvoices] = React.useState([]);
  const [paymentTab, setPaymentTab] = React.useState("pending");
  const [InvoiceList, setInvoiceList] = React.useState([]);
  const [paymentType, setPaymentType] = React.useState(""); // Track selected payment method
  const [totalAmount, setTotalAmount] = React.useState(0);
  const [isModalOpen, setModalOpen] = React.useState({
    state: false,
    data: {},
  });
  const [zelleModalOpen, setZelleModalOpen] = React.useState(false);
  const [showECheckModal, setShowECheckModal] = React.useState(false);
  const { userId } = props;
  const isClientSelf = !userId && localStorage.getItem("user_role") === "client";
  const {
    categoryTab,
    showCategoryChooser,
    syncClientType,
  } = useClientCategoryTab();

  const USER_DATA = userId
    ? props.USER_DATA
    : JSON.parse(localStorage.getItem("USER_DATA"));

  const [isButtonEnabled, setButtonEnabled] = React.useState(false); // State for enabling/disabling the button
  const [termsChecked, setTermsChecked] = React.useState(false); // State for terms checkbox
  const [agreementModalOpen, setAgreementModalOpen] = React.useState(false);
  const [canAgree, setCanAgree] = React.useState(false);
  const agreementScrollRef = React.useRef(null);
  const agreementSectionRefs = React.useRef([]);
  const invoicePrintRef = useRef();
  const handleInvoicePrint = useReactToPrint({
    content: () => invoicePrintRef.current,
  });
  const [activeAgreementSection, setActiveAgreementSection] = React.useState(
    AGREEMENT_CONTENT.sections[0]?.title || "",
  );

  useMemo(() => {
    if (location.state?.data)
      setModalOpen({ state: true, data: location.state.data });
  }, [location]);

  useEffect(() => {
    if (!isClientSelf) return;
    try {
      const userData = JSON.parse(localStorage.getItem("USER_DATA") || "{}");
      syncClientType(userData.client_type);
    } catch {
      /* ignore */
    }
  }, [isClientSelf, syncClientType]);

  useLayoutEffect(() => {
    const payload = {
      billed_to: userId || localStorage.getItem("user_id"),
    };
    if (showCategoryChooser) {
      payload.category = categoryTab;
    }
    dispatch(INVOICE_ACTION.getAllInvoices(payload));
  }, [userId, dispatch, categoryTab, showCategoryChooser]);

  const { allInvoices = [] } = useSelector((state) => state.invoice || {});

  useEffect(() => {
    const extractInvoiceNumber = (invoice_no = "") =>
      parseInt(String(invoice_no).replace("INV-", "")) || 0;

    const matchesCategory = (invoice) => {
      if (!invoice) return false;
      if (showCategoryChooser && !recordMatchesClientCategory(invoice, categoryTab)) {
        return false;
      }
      if (isInvoiceCancelled(invoice)) return false;
      return Number(invoice.total || 0) > 0;
    };

    const mapInvoiceRow = (data, { includeSelect }) => {
      const { invoice_no, total, invoice_date, amount_paid, amount_due } = data;

      const paid = Number(amount_paid || 0);
      const totalNum = Number(total || 0);
      const due =
        amount_due != null ? Number(amount_due) : Math.max(totalNum - paid, 0);

      if (includeSelect) {
        return {
          select: false,
          invoice_no,
          invoice_date: dateFormatter(invoice_date),
          amount: `$ ${totalNum.toFixed(2)}`,
          due_amount: `$ ${due.toFixed(2)}`,
        };
      }

      return {
        invoice_no,
        invoice_date: dateFormatter(invoice_date),
        amount: `$ ${totalNum.toFixed(2)}`,
        amount_paid: `$ ${paid.toFixed(2)}`,
      };
    };

    const sortByInvoiceNoDesc = (list) =>
      [...list].sort(
        (a, b) =>
          extractInvoiceNumber(b.invoice_no) - extractInvoiceNumber(a.invoice_no),
      );

    const payableInvoices = (allInvoices || []).filter((invoice) => {
      if (!matchesCategory(invoice)) return false;
      const statusLabel = getInvoicePaymentStatusLabel(invoice);
      return ["Pending", "Partially Paid"].includes(statusLabel);
    });

    const paidOnlyInvoices = (allInvoices || []).filter((invoice) => {
      if (!matchesCategory(invoice)) return false;
      return getInvoicePaymentStatusLabel(invoice) === "Paid";
    });

    setPendingInvoices(
      sortByInvoiceNoDesc(payableInvoices).map((data) =>
        mapInvoiceRow(data, { includeSelect: true }),
      ),
    );
    setPaidInvoices(
      sortByInvoiceNoDesc(paidOnlyInvoices).map((data) =>
        mapInvoiceRow(data, { includeSelect: false }),
      ),
    );
  }, [allInvoices, showCategoryChooser, categoryTab]);

  const invoices =
    isClientSelf && paymentTab === "paid" ? paidInvoices : pendingInvoices;

  const onChange = (value, key, rowIndex) => {
    const nextPending = [...pendingInvoices];
    nextPending[rowIndex][key] = value;
    setPendingInvoices(nextPending);
  };

  const hasSelectedInvoice = React.useMemo(
    () =>
      (!isClientSelf || paymentTab === "pending") &&
      pendingInvoices.some((invoice) => invoice.select),
    [pendingInvoices, isClientSelf, paymentTab],
  );

  useEffect(() => {
    if (!hasSelectedInvoice) {
      setTermsChecked(false);
      setAgreementModalOpen(false);
      setCanAgree(false);
    }
  }, [hasSelectedInvoice]);

  useEffect(() => {
    // Enable button only if invoice is selected AND terms are checked
    setButtonEnabled(hasSelectedInvoice && termsChecked);
  }, [hasSelectedInvoice, termsChecked]);

  const handleTermsCheckboxChange = (checked) => {
    if (checked) {
      if (agreementModalOpen) {
        setAgreementModalOpen(false);
        setCanAgree(false);
        return;
      }
      openAgreementModal();
      return;
    }
    setTermsChecked(false);
    setAgreementModalOpen(false);
    setCanAgree(false);
  };

  const openAgreementModal = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    setCanAgree(false);
    setActiveAgreementSection(AGREEMENT_CONTENT.sections[0]?.title || "");
    setAgreementModalOpen(true);
  };

  useEffect(() => {
    if (!agreementModalOpen) return;

    const scrollEl = agreementScrollRef.current;
    if (!scrollEl) return;

    scrollEl.scrollTop = 0;
    setCanAgree(false);

    const raf = requestAnimationFrame(() => {
      if (scrollEl.scrollHeight <= scrollEl.clientHeight + 16) {
        setCanAgree(true);
      }
    });

    return () => cancelAnimationFrame(raf);
  }, [agreementModalOpen]);

  const updateActiveAgreementSection = (scrollElement) => {
    if (!scrollElement) return;

    const scrollTop = scrollElement.scrollTop;
    const activationOffset = Math.max(
      140,
      Math.round(scrollElement.clientHeight * 0.28),
    );
    let nextSection = AGREEMENT_CONTENT.sections[0]?.title || "";

    agreementSectionRefs.current.forEach((sectionNode, index) => {
      if (
        sectionNode &&
        sectionNode.offsetTop <= scrollTop + activationOffset
      ) {
        nextSection = AGREEMENT_CONTENT.sections[index]?.title || nextSection;
      }
    });

    setActiveAgreementSection(nextSection);
  };

  const handleAgreementScroll = (e) => {
    updateActiveAgreementSection(e.currentTarget);

    const { scrollTop, clientHeight, scrollHeight } = e.currentTarget;
    const fitsWithoutScroll = scrollHeight <= clientHeight + 16;
    const atBottom = scrollTop + clientHeight >= scrollHeight - 16;
    setCanAgree(fitsWithoutScroll || atBottom);
  };

  const scrollAgreementToBottom = () => {
    const scrollEl = agreementScrollRef.current;
    if (!scrollEl) return;

    scrollEl.scrollTo({ top: scrollEl.scrollHeight, behavior: "smooth" });

    const finishScroll = () => {
      scrollEl.scrollTop = scrollEl.scrollHeight;
      updateActiveAgreementSection(scrollEl);
      setCanAgree(true);
    };

    if (scrollEl.scrollHeight <= scrollEl.clientHeight + 16) {
      finishScroll();
      return;
    }

    window.setTimeout(finishScroll, 450);
  };

  const handleAgreementAccept = () => {
    setTermsChecked(true);
    setCanAgree(false);
    setAgreementModalOpen(false);
  };

  const total = useMemo(() => {
    return pendingInvoices.reduce(
      (accumulator, currentValue) =>
        accumulator +
        (currentValue.select
          ? Number(currentValue.due_amount.replace(/[^0-9.]/g, ""))
          : 0),
      0,
    );
  }, [pendingInvoices]);
  // Zelle Payment Handler - Open Modal
  const handleZellePayment = () => {
    setZelleModalOpen(true);
    setPaymentType("zelle");
    setTotalAmount(total); // Capture the total amount at the time of payment
  };

  const handleAuthorizeNetPayment = async () => {
    setPaymentType("authorize.net");

    const apiLoginId =
      process.env.REACT_APP_AUTHORIZE_NET_LOGIN_ID || "5Yb6Hf9Mh";
    const currencyCode = "USD";

    const selectedInvoices = pendingInvoices.filter((invoice) => invoice.select);
    if (selectedInvoices.length === 0) {
      alert("Please select at least one invoice to proceed.");
      return;
    }

    // Exact values for hash and form — same strings in both (Reason Code 99)
    const amountStr = total.toFixed(2);
    const timestampStr = String(Math.round(Date.now() / 1000));
    const sequenceStr = String(Math.floor(Math.random() * 100000));

    const hashApiUrl = process.env.REACT_APP_GENERATE_HASH_URL
      ? process.env.REACT_APP_GENERATE_HASH_URL
      : API_URL
        ? `${API_URL.replace(/\/api\/v1\/?$/, "")}/api/generate-hash`
        : `${window.location.origin}/api/generate-hash`;

    // URL on our backend that Authorize.Net will POST the relay response to.
    // Backend should verify the response and mark the listed invoices as Paid
    // when the transaction is successful.

    try {
      const response = await fetch(hashApiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          amount: amountStr,
          sequence: sequenceStr,
          timestamp: timestampStr,
          currencyCode: currencyCode,
        }),
      });

      const data = await response.json();

      if (!data.hashKey) {
        alert(data.message || "Error generating hash. Please try again.");
        return;
      }

      const gatewayUrl =
        process.env.REACT_APP_AUTHORIZE_NET_SANDBOX === "true"
          ? "https://test.authorize.net/gateway/transact.dll"
          : "https://secure.authorize.net/gateway/transact.dll";

      const form = document.createElement("form");
      form.method = "POST";
      form.action = gatewayUrl;

      // Same amount, sequence, timestamp, currency as in hash request
      const fields = {
        x_login: apiLoginId,
        x_amount: amountStr,
        x_fp_sequence: sequenceStr,
        x_fp_timestamp: timestampStr,
        x_currency_code: currencyCode,
        x_description: "Invoice Payment",
        x_invoice_num: selectedInvoices.map((inv) => inv.invoice_no).join(", "),
        x_fp_hash: data.hashKey,
        x_show_form: "PAYMENT_FORM",
        x_method: "eCheck",
        // Enable relay response so our backend can auto‑update invoice status.
        x_relay_response: "TRUE",
        x_relay_url: "https://app.finlotax.com/api/payment/authorize-net/relay",
      };

      Object.entries(fields).forEach(([key, value]) => {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = key;
        input.value = value;
        form.appendChild(input);
      });

      document.body.appendChild(form);
      form.submit();
    } catch (error) {
      console.error("Authorize.Net Payment Error:", error);
      alert("Failed to generate payment request.");
    }
  };

  // "Pay with authorize.net" button
  // const handleAuthorizeNetPayment = async () => {
  //   setPaymentType("authorize.net");
  //   setTotalAmount(total);
  //   setShowECheckModal(true);
  // };

  // Called when the user successfully pays with eCheck
  // const handleECheckSuccess = (response) => {
  //   console.log("ECheck payment success:", response);
  //   // ... you can refresh invoice data, show success message, etc.
  //   setShowECheckModal(false);
  // };

  // // Called when there's an error
  // const handleECheckError = (error) => {
  //   console.log("ECheck payment error:", error.transactionResponse, error.messages);
  // };

  // SwipePay Handler - Redirect
  // const handleSwipePay = () => {
  //   setPaymentType("swipepay");
  //   window.location.href = "https://www.swipepay.com/payment";  // Replace with actual SwipePay URL
  // };
  const onActionClick = (action, row, actionIndex) => {
    const invoice = allInvoices.find(
      (inv) => inv.invoice_no === row.invoice_no,
    );

    if (invoice) {
      setModalOpen({ state: true, data: invoice });
    }
  };

  const isPaidTab = isClientSelf && paymentTab === "paid";

  const pendingFields = [
    {
      type: "checkbox",
      changeData: onChange,
    },
    {
      type: "label",
    },
    {
      type: "label",
    },
    {
      type: "label",
    },
    {
      type: "label",
    },
  ];

  const paidFields = [
    {
      type: "label",
    },
    {
      type: "label",
    },
    {
      type: "label",
    },
    {
      type: "label",
    },
  ];

  const tableHeader = isPaidTab
    ? [
        { id: "1", label: "View" },
        { id: "2", label: "Description/Invoice Ref #" },
        { id: "3", label: "Invoice Date" },
        { id: "4", label: "Net Amount" },
        { id: "5", label: "Amount Paid" },
      ]
    : [
        { id: "1", label: "View" },
        { id: "2", label: "Select" },
        { id: "3", label: "Description/Invoice Ref #" },
        { id: "4", label: "Invoice Date" },
        { id: "5", label: "Net Amount" },
        { id: "6", label: "Due Amount" },
      ];

  return (
    <div className="payment-page-wrapper">
      <Grid container spacing={3} className="payment-page-content">
        <Grid item xs={12} className="payment-page">
          <div className="payment-panel">
            {isClientSelf && (
              <div className="payment-status-tabs">
                <button
                  type="button"
                  className={`payment-status-tab ${
                    paymentTab === "pending" ? "is-active" : ""
                  }`}
                  onClick={() => setPaymentTab("pending")}
                >
                  Pending
                </button>
                <button
                  type="button"
                  className={`payment-status-tab ${
                    paymentTab === "paid" ? "is-active" : ""
                  }`}
                  onClick={() => setPaymentTab("paid")}
                >
                  Paid
                </button>
              </div>
            )}
            <div className="payment-panel-heading">
              <h3 className="payment-panel-title">
                {isPaidTab
                  ? "Paid invoices"
                  : "Select the invoice / Invoice you want to pay"}
              </h3>
              <div className="payment-panel-tools">
                {/* <IconButton className="payment-icon-btn" aria-label="Filter">
                  <FilterListOutlinedIcon />
                </IconButton> */}
              </div>
            </div>
            <Table
              variant="small"
              title=""
              header={tableHeader}
              data={invoices}
              fields={isPaidTab ? paidFields : pendingFields}
              onActionClick={onActionClick}
              aligncss="payment-table"
              icons={{ eyeIcon: true }}
              customRow={
                isPaidTab ? null : (
                  <>
                    <TableCell /> {/* View (eye icon) */}
                    <TableCell /> {/* Select (checkbox) */}
                    <TableCell>
                      <strong>Total</strong>
                    </TableCell>
                    <TableCell /> {/* Invoice Date */}
                    <TableCell /> {/* Net Amount */}
                    <TableCell>
                      <strong>$ {total.toFixed(2)}</strong>
                    </TableCell>
                  </>
                )
              }
            />
          </div>
        </Grid>
        {hasSelectedInvoice && (
          <>
        {/* Terms checkbox */}
        <Grid item xs={12} className="agreement-row">
          <Labelbox
            type="checkbox"
            checked={termsChecked}
            changeData={handleTermsCheckboxChange}
          />
          <span className="terms agreement-text">
            I have read the{" "}
            <button
              type="button"
              onMouseDown={(event) => event.preventDefault()}
              onClick={(event) => openAgreementModal(event)}
              className="agreement-link-button"
            >
              Terms & Conditions,
            </button>{" "}
            <button
              type="button"
              onMouseDown={(event) => event.preventDefault()}
              onClick={(event) => openAgreementModal(event)}
              className="agreement-link-button"
            >
              Privacy Policy,
            </button>{" "}
            <button
              type="button"
              onMouseDown={(event) => event.preventDefault()}
              onClick={(event) => openAgreementModal(event)}
              className="agreement-link-button"
            >
              Disclaimer
            </button>{" "}
            and approve this contract proposal for processing
          </span>
        </Grid>
        {/* Notes section */}
        <Grid item xs={12}>
          <Paper className="payment-notes">
            <span className="payment-notes-msg">Notes:</span>
            <ul>
              <li>
                Please ensure that you are sending the payment to the correct
                recipient - billing@finlotax.com
              </li>
              <li>
                Transactions made via Zelle are instant and cannot be reversed
              </li>
              <li>
                Authorize.net is third party merchant payment option. We do not
                record or save these details
              </li>
            </ul>
          </Paper>
        </Grid>

        <Grid item xs={12} className="flex flex-center">
          <Button
            disable={!isButtonEnabled}
            text={
              <div>
                <img
                  src={require("../../images/zelle-logo.png")}
                  alt="Zelle"
                  style={{ width: "30px", marginRight: "10px" }}
                />
                Pay with Zelle
              </div>
            }
            large
            handleClick={handleZellePayment}
          />

          <Button
            disable={!isButtonEnabled}
            text={
              <div>
                <img
                  src={require("../../images/authorize.jpeg")}
                  alt="authorize.net"
                  style={{ width: "30px", marginRight: "10px" }}
                />
                Pay with authorize.net
              </div>
            }
            large
            handleClick={handleAuthorizeNetPayment}
          />
        </Grid>
          </>
        )}
      </Grid>

      {agreementModalOpen && (
        <Modal
          className="timesheet-view-modal agreement-terms-modal"
          open={agreementModalOpen}
          title={null}
          disableDismiss
          maxWidth
        >
          <div className="agreement-modal-header">
            <h2 className="agreement-modal-header-title">
              {activeAgreementSection}
            </h2>
            {!canAgree && (
              <button
                type="button"
                className="agreement-scroll-bottom-btn"
                onClick={scrollAgreementToBottom}
              >
                Scroll to bottom
              </button>
            )}
          </div>

          <div className="agreement-modal-body">
            <div
              className="agreement-modal-scroll"
              ref={agreementScrollRef}
              onScroll={handleAgreementScroll}
            >
              {AGREEMENT_CONTENT.sections.map((section, index) => (
                <div
                  key={section.title}
                  className="agreement-modal-section"
                  ref={(node) => {
                    agreementSectionRefs.current[index] = node;
                  }}
                >
                  {section.items ? (
                    <div className="agreement-modal-items">
                      {section.items.map((item) => (
                        <div key={item.title} className="agreement-modal-item">
                          <h3 className="agreement-modal-item-title">
                            {item.title}
                          </h3>
                          {item.blocks
                            ? renderAgreementBlocks(item.blocks, item.title)
                            : null}
                          {item.intro?.map((paragraph, index) => (
                            <p
                              key={`${item.title}-intro-${index}`}
                              className="agreement-modal-paragraph"
                            >
                              {renderTextWithEmails(paragraph)}
                            </p>
                          ))}
                          {item.lead && (
                            <p className="agreement-modal-paragraph">
                              {renderTextWithEmails(item.lead)}
                            </p>
                          )}
                          {item.bullets?.length ? (
                            <ul className="agreement-modal-list">
                              {item.bullets.map((bullet) => (
                                <li
                                  key={bullet}
                                  className="agreement-modal-list-item"
                                >
                                  {renderTextWithEmails(bullet)}
                                </li>
                              ))}
                            </ul>
                          ) : null}
                          {item.tail && (
                            <p className="agreement-modal-paragraph">
                              {renderTextWithEmails(item.tail)}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="agreement-modal-text">{section.body}</div>
                  )}
                </div>
              ))}

              <div className="agreement-modal-footer">
                <Button
                  text="I Agree"
                  handleClick={handleAgreementAccept}
                  disable={!canAgree}
                />
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* Existing Invoice Preview Modal */}
      {isModalOpen.state && (
        <Modal
          className="timesheet-view-modal"
          open={isModalOpen.state}
          // title="Invoice Preview"
          maxWidth
          // handleClose={() => setModalOpen({ state: false, data: {} })}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <h2 style={{ fontWeight: "bold" }}>Invoice Preview</h2>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Button
                text="Print"
                variant="outline"
                icon={<PrintRounded />}
                handleClick={() => handleInvoicePrint()}
              />
              <button
                className="close-btn"
                onClick={() => setModalOpen({ state: false, data: {} })}
              >
                ✕
              </button>
            </div>
          </div>
          <ContractInvoiceView data={isModalOpen.data} label="Invoice" />
        </Modal>
      )}

      {isModalOpen.data && Object.keys(isModalOpen.data).length > 0 && (
        <div style={{ display: "none" }}>
          <ContractInvoiceView
            inputRef={invoicePrintRef}
            id="divToPrint"
            data={isModalOpen.data}
            label="Invoice"
          />
        </div>
      )}

      {/* Zelle QR Code Modal */}
      {zelleModalOpen && paymentType === "zelle" && (
        <Modal
          className="timesheet-view-modal"
          open={zelleModalOpen}
          title="Scan QR Code to Pay with Zelle"
          handleClose={() => setZelleModalOpen(false)}
          maxWidth
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <h2 style={{ fontWeight: "bold" }}>
              Scan QR Code to Pay with Zelle
            </h2>
            <button
              className="close-btn"
              onClick={() => setZelleModalOpen(false)}
            >
              ✕
            </button>
          </div>
          <div
            className="qr-container"
            style={{
              fontSize: "18px",
              textAlign: "center",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <p style={{ margin: "10px 0" }}>
              Total Amount to be paid:{" "}
              <strong>${totalAmount.toFixed(2)}</strong>
            </p>
          </div>
          <Grid item xs={12}>
            <Paper className="payment-notes">
              <ul>
                <li>
                  Use your banking app or Zelle app to send your payment
                  securely.
                </li>
                <li>
                  Ensure the correct amount is entered before confirming the
                  transaction.
                </li>
              </ul>
            </Paper>
          </Grid>
          <div className="qr-container">
            <img src={ZelleQR} alt="Zelle QR Code" className="zelle-qr-image" />
          </div>
        </Modal>
      )}

      {/* ECheck Modal for Authorize.Net */}
      {/* <AuthorizeNetECheckForm
        open={showECheckModal}
        onClose={() => setShowECheckModal(false)}
        totalAmount={totalAmount}
        selectedInvoices={invoices.filter((inv) => inv.select)}
        onPaymentSuccess={handleECheckSuccess}
        onPaymentError={handleECheckError}
      /> */}
    </div>
  );
};

export default ContractPayment;
